'use client';

import { useEffect, useRef, useState } from 'react';
import {
  getCartAnalyticsContext,
  useAnalyticsEvent,
  type CartResponseResponse,
  type ProgressBarResponse,
} from '@skin-seoul/service';
import { useCallbackRef } from '@skin-seoul/utils';

export function useCartBenefitsTracking(
  progressBar: ProgressBarResponse | null,
  isTrackingActive: boolean,
  options?: {
    cart: CartResponseResponse['cart'];
    headline: string;
    uiLocation: 'cart_drawer' | 'unlock_reward_page';
  },
) {
  const analytics = useAnalyticsEvent();
  const [visible, setVisible] = useState(false);
  const lastHeadline = useRef<string | null>(null);
  const headline = options?.headline ?? JSON.stringify(progressBar);
  const emit = useCallbackRef(() => {
    analytics.trackCustom({
      eventName: 'progress_bar_interaction',
      skipGTM: true,
      params: {
        ...(options ? getCartAnalyticsContext(options.cart, options.uiLocation) : {}),
        progress_name: 'free_shipping_bmsm',
        source_component: 'BMSMCampaignBanner',
        ui_location: options?.uiLocation ?? 'cart_drawer',
        action: 'viewed',
        headline,
      },
    });
  });
  // Successful mutation responses own completed/lost; opening an unlocked bag is not a new unlock.
  useEffect(() => {
    if (!isTrackingActive) {
      lastHeadline.current = null;
      setVisible(false);
      return;
    }
    if (progressBar && visible && lastHeadline.current !== headline) {
      lastHeadline.current = headline;
      emit();
    }
  }, [progressBar, isTrackingActive, visible, headline, emit]);
  return { handleImpressionStart: () => setVisible(true), handleImpressionEnd: () => setVisible(false) };
}
